import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Button, Spin, Upload, notification } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Pencil } from 'lucide-react';
import { OcclusionStudyMode, ReviewMask } from '../../../sdk/@types/Revisao';
import FileService from '../../../sdk/services/FileService';
import useAuthedImage from '../../../core/hooks/useAuthedImage';
import { focoEmCampoDeTexto } from '../Revisao/SessaoControles';
import useMaskDraw from './useMaskDraw';
import OcclusionToolbar, {
  type OcclusionTool,
  OCCLUSION_ZOOM_MIN,
  OCCLUSION_ZOOM_MAX,
} from './OcclusionToolbar';
import MarcacoesPanel from './MarcacoesPanel';
import {
  aparenciaDaMarcacao,
  classeDaForma,
  classesDaMarcacao,
  textoDaMarcacao,
  CLASSES_DA_APARENCIA,
  GRADIENTE_PERGUNTADA,
  type AparenciaMarcacao,
} from './marcacaoAparencia';
import {
  MASK_REQUIRED_MESSAGE,
  STUDY_MODE_OPTIONS,
  askedIndex,
  indexAfterMove,
  indexAfterRemove,
  moveMask,
  previewCaption,
  relabelMask,
  removeMaskAt,
  resolveStudyMode,
  reviewUnitsMessage,
  type EditorFace,
} from './occlusionStudy';
import {
  atalhoDeHistorico,
  desfazer,
  empilhar,
  historicoVazio,
  podeDesfazer,
  podeRefazer,
  refazer,
  type HistoricoDeMarcacoes,
} from './historicoDeMarcacoes';

const { Dragger } = Upload;

export interface OcclusionEditorProps {
  /** Stored key of the uploaded image. */
  frontImageKey?: string;
  /** Masks, as % of the rendered image box. Controlled by the parent. */
  masks: ReviewMask[];
  onImageChange: (fileKey: string | undefined) => void;
  onMasksChange: (masks: ReviewMask[]) => void;
  /** Override image resolution. Defaults to an authenticated blob fetch. */
  resolveImageUrl?: (fileKey: string) => string;
  /** Override the upload. Defaults to FileService.upload(file, 'review_image'). */
  uploadImage?: (file: File) => Promise<string>;
  /** How the student faces hide markings. Omitted ⇒ internal state, starting at ocultar-todas. */
  studyMode?: OcclusionStudyMode;
  onStudyModeChange?: (mode: OcclusionStudyMode) => void;
  /**
   * The card's own `back` field, shown to the author as "Explicação (verso)"
   * (design.md, Decisão 8). Optional by spec — the caller is the one that
   * turns a whitespace-only value into an absent `back` at save time; this
   * field just shows whatever text it's given.
   */
  explanation: string;
  onExplanationChange: (value: string) => void;
}

const defaultUpload = (file: File) =>
  FileService.upload(file, 'review_image').then((r) => r.file_key);

/** Draws labelled masks over an uploaded image and previews both student faces. */
export default function OcclusionEditor({
  frontImageKey,
  masks,
  onImageChange,
  onMasksChange,
  resolveImageUrl,
  uploadImage = defaultUpload,
  studyMode,
  onStudyModeChange,
  explanation,
  onExplanationChange,
}: OcclusionEditorProps) {
  const explicacaoId = useId();
  const [face, setFace] = useState<EditorFace>('edit');
  const [tool, setTool] = useState<OcclusionTool>('rectangle');
  const [selected, setSelected] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(1); // 1 = 100%
  const [internalMode, setInternalMode] = useState<OcclusionStudyMode>(() =>
    resolveStudyMode(undefined),
  );
  const imageRef = useRef<HTMLImageElement | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);

  const [historico, setHistorico] = useState<HistoricoDeMarcacoes>(historicoVazio);
  // Which pointer gesture (if any) is live, and whether it already pushed its
  // one history entry — so a continuous move/resize drag stacks a single
  // undo step instead of one per pointermove.
  const gesto = useRef({ ativo: false, empilhado: false });

  // Local to this editor session, not a global app undo — cleared whenever the
  // image identity changes. Closing the editor clears it for free: this state
  // is never lifted above this component, so it is simply discarded on unmount
  // (design.md, Decisão 6).
  useEffect(() => {
    setHistorico(historicoVazio());
  }, [frontImageKey]);

  useEffect(() => {
    const encerrarGesto = () => {
      gesto.current = { ativo: false, empilhado: false };
    };
    window.addEventListener('pointerup', encerrarGesto);
    return () => window.removeEventListener('pointerup', encerrarGesto);
  }, []);

  const activeMode = resolveStudyMode(studyMode ?? internalMode);

  const { url: authedUrl, loading: loadingImage, error: imageError } = useAuthedImage(
    resolveImageUrl ? undefined : frontImageKey
  );
  const imageUrl = frontImageKey
    ? resolveImageUrl
      ? resolveImageUrl(frontImageKey)
      : authedUrl
    : null;

  const editing = face === 'edit';
  const asked = askedIndex(selected, masks.length);
  const unitsMessage = reviewUnitsMessage(masks.length);

  /** One push per discrete edit: relabel, reorder, remove. */
  const aplicarEdicao = (next: ReviewMask[]) => {
    setHistorico((h) => empilhar(h, masks));
    onMasksChange(next);
  };

  /** Fed to useMaskDraw as `onChange`. A move/resize drag calls this on every
   *  pointermove; only the first call of a gesture pushes to history, so one
   *  continuous drag is one undo step, never one per pointer event. */
  const aplicarDuranteGesto = (next: ReviewMask[]) => {
    if (!gesto.current.ativo || !gesto.current.empilhado) {
      setHistorico((h) => empilhar(h, masks));
      gesto.current.empilhado = true;
    }
    onMasksChange(next);
  };

  const { draft, handlePointerDown } = useMaskDraw({
    masks,
    selectedIndex: selected,
    imageRef,
    disabled: !editing,
    onChange: aplicarDuranteGesto,
    onSelect: setSelected,
  });

  const chooseStudyMode = (mode: OcclusionStudyMode) => {
    const resolved = resolveStudyMode(mode);
    if (studyMode === undefined) setInternalMode(resolved);
    onStudyModeChange?.(resolved);
  };

  // Defensive clamp — the toolbar should already clamp, but this file must never
  // trust a prop blindly for something that drives a visual transform.
  const changeZoom = (next: number) =>
    setZoom(Math.min(OCCLUSION_ZOOM_MAX, Math.max(OCCLUSION_ZOOM_MIN, next)));

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fileKey = await uploadImage(file);
      onImageChange(fileKey);
      onMasksChange([]);
      setSelected(null);
      setZoom(1);
    } catch {
      notification.error({ message: 'Erro ao enviar a imagem.' });
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (index: number) => {
    aplicarEdicao(removeMaskAt(masks, index));
    setSelected(indexAfterRemove(selected, index));
  };

  /** Delete/Backspace remove the selected mask — but never while the user is typing
   *  in one of the editor's own text fields (the answer input, a future explanation
   *  field). Reuses the session's own focus check so this doesn't grow a second one. */
  const handleEditorKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!editing || selected === null) return;
    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    if (focoEmCampoDeTexto(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    removeAt(selected);
  };

  const reorder = (from: number, to: number) => {
    aplicarEdicao(moveMask(masks, from, to));
    setSelected(indexAfterMove(selected, from, to, masks.length));
  };

  const setLabel = (index: number, label: string) => {
    aplicarEdicao(relabelMask(masks, index, label));
  };

  const clearImage = () => {
    onImageChange(undefined);
    onMasksChange([]);
    setSelected(null);
    setZoom(1);
  };

  const handleDesfazer = () => {
    const resultado = desfazer(historico, masks);
    if (!resultado) return;
    setHistorico(resultado.historico);
    onMasksChange(resultado.anterior);
    setSelected((s) => (s !== null && s < resultado.anterior.length ? s : null));
  };

  const handleRefazer = () => {
    const resultado = refazer(historico, masks);
    if (!resultado) return;
    setHistorico(resultado.historico);
    onMasksChange(resultado.proximo);
    setSelected((s) => (s !== null && s < resultado.proximo.length ? s : null));
  };

  // Stable listener, fresh handlers: the ref is updated every render, so the
  // keydown effect below can mount once instead of resubscribing on every
  // masks/historico change — same pattern useMaskDraw uses for its own
  // window listeners.
  const atalhosRef = useRef({ handleDesfazer, handleRefazer });
  atalhosRef.current = { handleDesfazer, handleRefazer };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (focoEmCampoDeTexto(event.target)) return;
      const atalho = atalhoDeHistorico(event);
      if (!atalho) return;
      event.preventDefault();
      if (atalho === 'desfazer') atalhosRef.current.handleDesfazer();
      else atalhosRef.current.handleRefazer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!frontImageKey) {
    return (
      <div className="max-w-xl min-w-0 w-full">
        <Dragger
          accept="image/png,image/jpeg"
          multiple={false}
          showUploadList={false}
          disabled={uploading}
          beforeUpload={(file) => {
            handleUpload(file);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon text-brand">
            {uploading ? <Spin /> : <InboxOutlined className="text-brand" />}
          </p>
          <p className="ant-upload-text text-ink">
            Arraste a imagem aqui ou clique para selecionar
          </p>
          <p className="ant-upload-hint text-ink-muted">
            PNG ou JPEG. Depois arraste sobre a imagem para criar as marcações.
          </p>
        </Dragger>
      </div>
    );
  }

  return (
    <div
      ref={editorRef}
      className="flex min-w-0 w-full flex-col gap-4 outline-none lg:flex-row lg:items-start"
      tabIndex={0}
      onKeyDown={handleEditorKeyDown}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex min-w-0 flex-col gap-2">
          <OcclusionToolbar
            tool={tool}
            onToolChange={setTool}
            face={face}
            onFaceChange={setFace}
            podeDesfazer={podeDesfazer(historico)}
            podeRefazer={podeRefazer(historico)}
            onDesfazer={handleDesfazer}
            onRefazer={handleRefazer}
            zoom={zoom}
            onZoomChange={changeZoom}
          />
          <Button size="small" className="w-full self-end sm:w-auto" onClick={clearImage}>
            Trocar imagem
          </Button>
        </div>

        <div
          className={
            // Centering an overflowing flex child can leave part of it unreachable by
            // scroll in some browsers. Past 100% the scaled image can overflow, so this
            // switches to start-aligned (matching the wrapper's own `origin-top-left`
            // below) so the whole image stays reachable; at <=100% it never overflows,
            // so the original centered look is unchanged.
            'flex max-h-[70vh] overflow-auto rounded-card border border-edge bg-surface-muted p-3 ' +
            (zoom > 1 ? 'items-start justify-start' : 'justify-center')
          }
        >
          {loadingImage && <Spin />}
          {imageError && (
            <p className="py-8 text-sm text-danger-ink">
              Não foi possível carregar a imagem.
            </p>
          )}
          {imageUrl && (
            <div
              // Scales as one unit with its overlay/masks below (shared ancestor — see
              // useMaskDraw's getBoundingClientRect, which reports this post-transform).
              // Anchored top-left, not the CSS default center, so it grows toward the
              // scrollable area above instead of away from it on every side.
              className="relative inline-block max-w-full origin-top-left"
              style={{ transform: `scale(${zoom})` }}
            >
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Imagem do cartão de oclusão"
                className="block max-h-[60vh] max-w-full rounded-card"
                draggable={false}
              />

              <div
                onPointerDown={(event) => {
                  // `handlePointerDown` calls preventDefault(), which also suppresses the
                  // browser's own "click a non-focusable element → focus its nearest
                  // focusable ancestor" step — so drawing or selecting a mask here would
                  // otherwise never move focus into `editorRef`, and Delete/Backspace would
                  // never reach `handleEditorKeyDown`. Claim it ourselves instead.
                  editorRef.current?.focus();
                  gesto.current = { ativo: true, empilhado: false };
                  handlePointerDown(event);
                }}
                className={
                  'absolute inset-0 touch-none ' + (editing ? 'cursor-crosshair' : 'pointer-events-none')
                }
              >
                {masks.map((mask, i) => {
                  const isSelected = selected === i;
                  const cores = classesDaMarcacao(i);
                  let skin: string;
                  let content: string | number;
                  let papel: AparenciaMarcacao | undefined;

                  if (face === 'edit') {
                    skin = isSelected ? cores.retanguloSelecionado : cores.retangulo;
                    content = i + 1;
                  } else {
                    // Same rule the review session reads — never a second covered/revealed
                    // branch here (design, Decisão 3): a drifted copy is how a preview lies
                    // about what the student will actually see.
                    papel = aparenciaDaMarcacao(face, activeMode, i, asked, 'estudo');
                    skin = CLASSES_DA_APARENCIA[papel];
                    content = textoDaMarcacao(papel, i, mask.label);
                  }

                  return (
                    <div
                      key={i}
                      data-mask-papel={papel}
                      className={
                        `pointer-events-none absolute flex items-center justify-center ${classeDaForma(mask.shape)} border-2 px-1 text-center text-xs font-semibold ` +
                        skin
                      }
                      style={{
                        left: `${mask.x}%`,
                        top: `${mask.y}%`,
                        width: `${mask.w}%`,
                        height: `${mask.h}%`,
                        ...(papel === 'perguntada-coberta' ? { background: GRADIENTE_PERGUNTADA } : {}),
                      }}
                    >
                      {face === 'edit' ? (
                        <span
                          className={
                            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold ' +
                            cores.numeroBadge
                          }
                        >
                          {i + 1}
                        </span>
                      ) : (
                        <span className="max-h-full overflow-hidden break-words leading-tight">
                          {content}
                        </span>
                      )}
                      {editing && isSelected && mask.label.trim() !== '' && (
                        <span className="pointer-events-none absolute -top-7 left-0 flex max-w-[10rem] items-center gap-1 rounded-input border border-select bg-surface px-2 py-1 text-xs text-ink shadow-md">
                          <Pencil size={11} className="shrink-0" aria-hidden />
                          <span className="min-w-0 truncate">{mask.label}</span>
                        </span>
                      )}
                      {editing && isSelected && (
                        <>
                          <span className="pointer-events-auto absolute -top-1 -left-1 h-3 w-3 cursor-nwse-resize rounded-sm border border-surface bg-brand" />
                          <span className="pointer-events-auto absolute -top-1 -right-1 h-3 w-3 cursor-nesw-resize rounded-sm border border-surface bg-brand" />
                          <span className="pointer-events-auto absolute -bottom-1 -left-1 h-3 w-3 cursor-nesw-resize rounded-sm border border-surface bg-brand" />
                          <span className="pointer-events-auto absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-sm border border-surface bg-brand" />
                          <span className="pointer-events-auto absolute top-1/2 -right-1 h-3 w-3 -translate-y-1/2 cursor-ew-resize rounded-sm border border-surface bg-brand" />
                          <span className="pointer-events-auto absolute -bottom-1 left-1/2 h-3 w-3 -translate-x-1/2 cursor-ns-resize rounded-sm border border-surface bg-brand" />
                        </>
                      )}
                    </div>
                  );
                })}

                {draft && (
                  <div
                    className={
                      'pointer-events-none absolute rounded border-2 ' +
                      classesDaMarcacao(masks.length).retangulo
                    }
                    style={{
                      left: `${draft.x}%`,
                      top: `${draft.y}%`,
                      width: `${draft.w}%`,
                      height: `${draft.h}%`,
                    }}
                  >
                    <span
                      data-draft-measure=""
                      className="pointer-events-none absolute -top-7 left-0 whitespace-nowrap rounded-input border border-select bg-surface px-2 py-1 font-mono text-xs tabular-nums text-ink shadow-md"
                    >
                      {Math.round(draft.w)}% × {Math.round(draft.h)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {editing && (
          <p className="text-xs text-ink-muted">
            Arraste sobre a imagem para criar uma marcação. Clique numa marcação para
            movê-la, ou arraste uma das alças para redimensionar.
          </p>
        )}
        {(face === 'front' || face === 'back') && masks.length > 0 && (
          <p className="text-sm text-ink-secondary">
            {previewCaption(face, asked, masks.length)}
          </p>
        )}
      </div>

      <div className="flex w-full flex-col gap-3 lg:w-96 lg:shrink-0">
        <div
          role="radiogroup"
          aria-label="Modo de estudo"
          data-study-mode={activeMode}
          className="flex flex-col gap-2"
        >
          {STUDY_MODE_OPTIONS.map((option) => {
            const checked = activeMode === option.value;
            return (
              <label
                key={option.value}
                className={
                  'flex cursor-pointer flex-col gap-1 rounded-card border p-3 ' +
                  (checked
                    ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                    : 'border-edge bg-surface text-ink')
                }
              >
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="radio"
                    name="occlusion-study-mode"
                    value={option.value}
                    checked={checked}
                    onChange={() => chooseStudyMode(option.value)}
                    className="shrink-0"
                  />
                  {option.label}
                </span>
                <span className="text-sm text-ink-secondary">{option.effect}</span>
              </label>
            );
          })}
        </div>

        {masks.length > 0 && unitsMessage && (
          <p className="text-sm text-ink" data-review-units={masks.length}>
            {unitsMessage}
          </p>
        )}

        {masks.length === 0 && (
          <p
            data-mask-required=""
            className="rounded-card border border-warn/40 bg-warn/[0.08] px-3 py-3 text-sm text-warn-ink"
          >
            {MASK_REQUIRED_MESSAGE}
          </p>
        )}

        <MarcacoesPanel
          masks={masks}
          selected={selected}
          onSelect={setSelected}
          onLabelChange={setLabel}
          onReorder={reorder}
          onRemove={removeAt}
        />

        <div className="flex flex-col gap-2">
          <label htmlFor={explicacaoId} className="text-sm font-medium text-ink">
            Explicação (verso)
          </label>
          <textarea
            id={explicacaoId}
            rows={3}
            value={explanation}
            onChange={(event) => onExplanationChange(event.target.value)}
            placeholder="Opcional — aparece abaixo do rótulo da marcação respondida, no verso."
            className="w-full resize-y rounded-input border border-edge bg-surface px-3 py-2 text-sm text-ink"
          />
        </div>
      </div>
    </div>
  );
}
